import React, { useState, ReactElement } from "react"
import Draggable from "react-draggable"
import clsx from "clsx"
import {
	ThemeProvider,
	Typography,
	Avatar,
	Divider,
	Grid,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	Paper,
	PaperProps
} from "@material-ui/core"
import {
	Phone as StartCallIcon,
	CallEnd as EndCallIcon,
	Close as CloseIcon,
	Minimize as MinimizeIcon,
	Fullscreen as RestoreIcon
} from "@material-ui/icons"
import { Alert } from "@material-ui/lab"

import theme from "@/styles/theme"

import { isComponentMounted, renderComponent, unmountComponent } from "@/utils/node"

import { PopConfirm } from "@/components"

import useStyles from "@/components/VoiceCall/VoiceCallDialog/styles"

import useDidMount from "@/hooks/useDidMount"
import useVoiceCall, { CallState } from "@/hooks/useVoiceCall"
import useContactDisplay from "@/hooks/useContactDisplay"

const VOICE_CALL_DIALOG_TITLE_ID = "voice-call-dialog-title"

export type VoiceCallDialogProps = {
	phoneNumber: string
	name: string
	pictureUrl?: string
	instanceId: number
	mustBlock: boolean
	isLoading: boolean
}

type VoiceCallDialogType = {
	open: (props: VoiceCallDialogProps) => void
	close: () => void
	isOpened: () => boolean
}

const VOICE_CALL_DIALOG_COMPONENT_ID = "voice-call-dialog"
const TEXT_INPUT_CONTAINER_ID = "text-input-container"
const MINIMIZED_FALLBACK_BOTTOM_IN_PIXELS = 24
const MINIMIZED_FALLBACK_RIGHT_IN_PIXELS = 24

/**
 * WARNING:
 * - Create draggable component outside main component to avoid
 * the dialog being aligned in center in every re-render.
 */
const VoiceCallDialogPaperComponent = (paperProps: PaperProps) => (
	<Draggable handle={`#${VOICE_CALL_DIALOG_TITLE_ID}`} cancel="[class*=MuiDialogContent-root]">
		<Paper {...paperProps} />
	</Draggable>
)

const VoiceCallDialog: VoiceCallDialogType & React.FC<VoiceCallDialogProps> = (props) => {
	const {
		phoneNumber,
		name,
		pictureUrl,
		instanceId,
		mustBlock,
		isLoading
	} = props

	const {
		callState,
		formattedElapsedSeconds,
		billedCredits,
		startCall,
		endCall,
		loadWebphone,
		webphoneContainer
	} = useVoiceCall({ instanceId, isLoading, mustBlock })

	const { getDisplayPhoneNumber } = useContactDisplay()

	const classes = useStyles()

	const [isOpen, setIsOpen] = useState(false)
	const [isMinimized, setIsMinimized] = useState(false)
	const [minimizedPosition, setMinimizedPosition] = useState<{ bottom: number; right: number } | null>(null)

	const handleStartCall = () => {
		startCall(phoneNumber)
	}

	const handleCloseImmediately = () => {
		endCall()

		setIsOpen(false)
		VoiceCallDialog.close()
	}

	const handleClose = () => {
		const isCallInProgress = callState !== "idle"

		if (isCallInProgress) {
			PopConfirm.open({
				title: "Encerrar ligação?",
				description: "Existe uma ligação em andamento. Ao fechar o modal, a ligação será encerrada.",
				confirmButtonText: "ENCERRAR LIGAÇÃO",
				onConfirm: handleCloseImmediately
			})
			return
		}

		handleCloseImmediately()
	}

	const handleDialogClose = (event: unknown, reason: "backdropClick" | "escapeKeyDown") => {
		const isBackdropClick = reason === "backdropClick"

		if (isBackdropClick) {
			return
		}

		handleClose()
	}

	const handleToggleMinimize = () => {
		const isGoingToMinimize = !isMinimized

		if (isGoingToMinimize) {
			const textInputElement = document.getElementById(TEXT_INPUT_CONTAINER_ID)

			if (textInputElement) {
				const rect = textInputElement.getBoundingClientRect()
				setMinimizedPosition({
					bottom: window.innerHeight - rect.top,
					right: window.innerWidth - rect.right
				})
			} else {
				setMinimizedPosition(null)
			}
		}

		setIsMinimized(isGoingToMinimize)
	}

	useDidMount(async () => {
		setIsOpen(true)

		await loadWebphone()
	})

	const getCallStateData = () => {
		const callStateToData: Record<CallState, {
			statusLabel: string
			statusClassName: string
			showPulseRings: boolean
			showTimer: boolean
			actionLabel: string
			actionIcon: ReactElement
			actionFabClassName: string
			actionHandler: () => void
		}> = {
			idle: {
				statusLabel: "Toque em ligar para iniciar",
				statusClassName: "",
				showPulseRings: false,
				showTimer: false,
				actionLabel: "Ligar",
				actionIcon: <StartCallIcon />,
				actionFabClassName: classes.fabGreen,
				actionHandler: handleStartCall
			},
			loading: {
				statusLabel: "Preparando chamada...",
				statusClassName: classes.callStatusCalling,
				showPulseRings: true,
				showTimer: false,
				actionLabel: "Cancelar",
				actionIcon: <EndCallIcon />,
				actionFabClassName: classes.fabRed,
				actionHandler: endCall
			},
			calling: {
				statusLabel: "Ligando...",
				statusClassName: classes.callStatusCalling,
				showPulseRings: true,
				showTimer: false,
				actionLabel: "Cancelar",
				actionIcon: <EndCallIcon />,
				actionFabClassName: classes.fabRed,
				actionHandler: endCall
			},
			active: {
				statusLabel: "Em chamada",
				statusClassName: classes.callStatusConnected,
				showPulseRings: false,
				showTimer: true,
				actionLabel: "Desligar",
				actionIcon: <EndCallIcon />,
				actionFabClassName: classes.fabRed,
				actionHandler: endCall
			}
		}

		return callStateToData[callState]
	}

	const callStateData = getCallStateData()

	return (
		<ThemeProvider theme={theme}>
			<Dialog
				open={isOpen}
				onClose={handleDialogClose}
				disableEnforceFocus
				PaperComponent={VoiceCallDialogPaperComponent}
				PaperProps={{
					className: clsx({
						[classes.paper]: true,
						[classes.paperMinimized]: isMinimized
					}),
					...(isMinimized && {
						style: {
							bottom: minimizedPosition?.bottom ?? MINIMIZED_FALLBACK_BOTTOM_IN_PIXELS,
							right: minimizedPosition?.right ?? MINIMIZED_FALLBACK_RIGHT_IN_PIXELS
						}
					})
				}}
				classes={{
					root: classes.modalRoot
				}}
				aria-labelledby={VOICE_CALL_DIALOG_TITLE_ID}
				hideBackdrop
				disableScrollLock
				maxWidth="xs"
				fullWidth={!isMinimized}
			>
				{webphoneContainer}

				<DialogTitle
					id={VOICE_CALL_DIALOG_TITLE_ID}
					className={clsx({
						[classes.dialogTitle]: true,
						[classes.dialogTitleMinimized]: isMinimized
					})}
					disableTypography
				>
					<Typography variant="h6">Ligar para contato</Typography>

					<Grid className={classes.dialogTitleActions}>
						<Grid
							container
							spacing={1}
						>
							<Grid item>
								<IconButton onClick={handleToggleMinimize} className={classes.dialogTitleActionIconButton}>
									{isMinimized ? <RestoreIcon /> : <MinimizeIcon />}
								</IconButton>
							</Grid>

							<Grid item>
								<IconButton onClick={handleClose} className={classes.dialogTitleActionIconButton}>
									<CloseIcon />
								</IconButton>
							</Grid>
						</Grid>
					</Grid>
				</DialogTitle>

				{isMinimized ? (
					<DialogContent className={classes.dialogContentMinimized}>
						<Grid container wrap="nowrap" alignItems="center" justifyContent="space-between" spacing={2}>
							<Grid item xs zeroMinWidth>
								<Grid container wrap="nowrap" alignItems="center" spacing={2}>
									<Grid item>
										<Avatar src={pictureUrl} className={classes.avatarMinimized} />
									</Grid>

									<Grid item xs zeroMinWidth>
										<Typography variant="body1" noWrap>{name}</Typography>

										<Typography variant="caption" className={callStateData.statusClassName} noWrap>
											{callStateData.showTimer ? formattedElapsedSeconds : callStateData.statusLabel}
										</Typography>
									</Grid>
								</Grid>
							</Grid>

							<Grid item>
								<IconButton className={callStateData.actionFabClassName} onClick={callStateData.actionHandler}>
									{callStateData.actionIcon}
								</IconButton>
							</Grid>
						</Grid>
					</DialogContent>
				) : (
					<DialogContent className={classes.dialogContent}>
						<Grid container direction="column" alignItems="center" spacing={3}>
							<Grid item>
								<Grid container direction="column" alignItems="center" spacing={2}>
									<Grid item>
										<Grid className={classes.avatarContainer}>
											{callStateData.showPulseRings && (
												<>
													<Grid className={classes.pulseRing} />
												</>
											)}

											<Avatar src={pictureUrl} className={classes.avatar} />
										</Grid>
									</Grid>

									<Grid item>
										<Grid container direction="column" alignItems="center">
											<Grid item>
												<Typography variant="h4">{name}</Typography>
											</Grid>

											<Grid item>
												<Typography variant="body2" color="textSecondary">{getDisplayPhoneNumber(phoneNumber)}</Typography>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>

							{callState === "active" && (
								<Grid item className={classes.creditBalanceIndicatorContainer}>
									<Grid container alignItems="center">
										<Typography variant="caption" color="textSecondary" align="center">
											Até o momento,
											{" "}
											<b>
												{billedCredits}
												{" créditos"}
											</b>
											{" "}
											foram consumidos.
											<br />
											Não se preocupe, caso essa ligação alcançar o limite do plano, ela não será interrompida.
										</Typography>
									</Grid>
								</Grid>
							)}

							<Grid item>
								<Grid container direction="column" alignItems="center" spacing={1}>
									<Grid item>
										<Typography variant="caption" className={callStateData.statusClassName}>
											{callStateData.statusLabel}
										</Typography>
									</Grid>

									{callStateData.showTimer && (
										<Grid item>
											<Typography className={classes.timer}>{formattedElapsedSeconds}</Typography>
										</Grid>
									)}
								</Grid>
							</Grid>

							<Grid item className={classes.dividerContainer}>
								<Divider orientation="horizontal" variant="fullWidth" />
							</Grid>

							<Grid item xs={12}>
								<Alert icon={false} severity="warning">
									<b>Importante:</b>
									<br />
									- O número utilizado para realizar a ligação é gerado automaticamente pelo serviço de voz.
									<br />
									- Chamadas que caem na caixa postal são cobradas caso haja conversação.
								</Alert>
							</Grid>

							<Grid item>
								<Grid container direction="column" alignItems="center">
									<Grid item>
										<IconButton className={callStateData.actionFabClassName} onClick={callStateData.actionHandler}>
											{callStateData.actionIcon}
										</IconButton>
									</Grid>
									<Grid item>
										<Typography variant="caption">{callStateData.actionLabel}</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</DialogContent>
				)}
			</Dialog>
		</ThemeProvider>
	)
}

VoiceCallDialog.open = (props: VoiceCallDialogProps) => {
	renderComponent(
		VOICE_CALL_DIALOG_COMPONENT_ID,
		<VoiceCallDialog {...props} />
	)
}

VoiceCallDialog.isOpened = () => isComponentMounted(VOICE_CALL_DIALOG_COMPONENT_ID)

VoiceCallDialog.close = () => {
	unmountComponent(VOICE_CALL_DIALOG_COMPONENT_ID)
}

export default VoiceCallDialog
